import React, { FC, useCallback, useEffect } from 'react';
import { Field, FieldRenderProps, Form } from 'react-final-form';
import { HotKeys } from 'react-hotkeys';
import type { Config } from 'final-form';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import Dropdown, { DropdownOption } from 'src/common/components/DropdownDS22';
import getClassName from 'src/common/utils/getClassName';
import boatImage from 'src/img/boat.svg';
import { MESSAGE_MAX_LENGTH, PAGES } from 'src/inbox/constants';
import extractTextFromHTML from 'src/common/utils/extractTextFromHTML';
import { fixMalformedHtml } from 'src/common/components/inputs/TextArea/plugins/ValueAndOnChangePlugin/ValueAndOnChangePluginHelper';
import type { CancelValues, ImmutableTrip } from 'src/types/trips/Trips';
import { PATHS } from 'src/common/constants';
import Button from 'src/common/components/Button';
import FormButtons from 'src/common/components/FormButtons';
import useDispatch from 'src/common/hooks/useAppDispatch';
import { clearOffer } from 'src/inbox/ducks/offer';
import { userIsOwner } from 'src/inbox/helpers';
import { trackEvent } from 'src/common/tracking';
import GA_EVENTS from 'src/common/components/UpdateAvailability/constants';
import { TRIP_ACTIONS, type TripActionName } from 'src/inbox/type/TripAction';
import { rffValidators } from '../../../../common/validation';
import NeedHelp from '../../NeedHelp';
import TextArea from '../../../../common/components/inputs/TextArea';
import FormError from '../../../../common/components/FormError';
import CommentMessage from '../../CommentMessage';
import { CANCEL_ACTIONS } from '../../TripMenu/actions';
import s from './TripAction.module.scss';

type TripActionFormProps = {
  fieldName: string;
  onSubmit: Config<CancelValues>['onSubmit'];
  closeBehavior: () => unknown;
  content: {
    action: TripActionName;
    title: string;
    description?: string;
    submitText?: string;
    cancelText?: string;
    placeholder?: string;
  };
  withMessageField?: boolean;
  messageLabel?: string;
  dropdownFieldName?: string;
  dropdownOptions?: DropdownOption[];
  dropdownPlaceholder?: string;
  withDropdownField?: boolean;
  withMessageCharacterCounter?: boolean;
  offerAnotherDate?: boolean;
  setOfferAnotherDate?: (value: boolean) => void;
  trip: ImmutableTrip;
};

const messageValidate = (
  action: TripActionName,
) => rffValidators.required({
  message: CANCEL_ACTIONS.includes(action)
    ? 'Please specify your reason before cancelling this trip.'
    : 'Please enter your message',
});

const dropdownValidate = (value: string) => (value ? undefined : 'Please select your reason before cancelling.');

const DropdownField: FC<FieldRenderProps<any>> = ({
  input,
  meta: { error, submitFailed },
  options,
  dropdownPlaceholder,
}) => {
  const withError = !!error && submitFailed;
  return (
    <div className={s.dropdownWrapper}>
      <Dropdown
        onChange={(selectedOption) => input.onChange(selectedOption?.id)}
        options={options}
        variant="white"
        instructionLabel={dropdownPlaceholder || 'Select Option'}
        submitting={input.submitting}
        error={withError}
        fullWidth
      />
      {withError && (
        <FormError error={error} withIcon />
      )}
    </div>
  );
};

const TripActionForm: FC<TripActionFormProps> = ({
  closeBehavior,
  content: { action, title, description, submitText, cancelText, placeholder },
  fieldName,
  messageLabel,
  dropdownFieldName,
  dropdownOptions,
  dropdownPlaceholder,
  onSubmit,
  withMessageField = true,
  withDropdownField = false,
  withMessageCharacterCounter,
  offerAnotherDate = false,
  setOfferAnotherDate = () => {},
  trip,
}) => {
  const { push } = useHistory();
  const tripId = trip.get('pk');
  const isCancellation = (userIsOwner(trip))
    ? CANCEL_ACTIONS.includes(action)
    : action === TRIP_ACTIONS.CANCEL_RESERVATION;
  const isSendMessage = action === TRIP_ACTIONS.SEND_MESSAGE;
  const isDeclineInquiry = action === TRIP_ACTIONS.DECLINE_INQUIRY;
  const dispatch = useDispatch();

  const handleDeclineInquiry = useCallback(() => {
    dispatch(clearOffer());
    trackEvent(GA_EVENTS.OFFER_ANOTHER_DATE_MODAL.DECLINE);
  }, [dispatch]);

  const handleOfferAnotherDateClick = useCallback(() => {
    closeBehavior();
    push(`${PATHS.INBOX}${tripId}/${PAGES.EDIT}/`);
    trackEvent(GA_EVENTS.OFFER_ANOTHER_DATE_MODAL.OFFER_DATE);
  }, [closeBehavior, push, tripId]);

  useEffect(() => {
    if (offerAnotherDate) {
      trackEvent(GA_EVENTS.OFFER_ANOTHER_DATE_MODAL.OPEN);
    }
  }, [offerAnotherDate]);

  return (
    <Form<CancelValues>
      onSubmit={onSubmit}
    >
      {({
        error,
        handleSubmit,
        submitError,
        submitting,
        submitFailed,
        values,
        form,
      }) => {
        const getMessageTextOnly = () => {
          const fieldValue = (values as Record<string, any>)[fieldName];
          if (!fieldValue) return '';
          const fixed = fixMalformedHtml(fieldValue);
          return extractTextFromHTML(fixed);
        };
        const messageCounter = getMessageTextOnly() ? getMessageTextOnly().length : 0;

        return (
          <form
            method="POST"
            onSubmit={handleSubmit}
            className={s.root}
          >
            <HotKeys
              handlers={{ submit: handleSubmit }}
              keyMap={{ submit: ['ctrl+enter', 'meta+enter'] }}
              className={s.wrapper}
            >
              {/* Step 1: Form Fields */}
              {!offerAnotherDate && (
                <>
                  <div>
                    <h3 className={s.title}>
                      {title}
                    </h3>

                    <label
                      htmlFor={withMessageField && !isCancellation ? fieldName : undefined}
                      className={getClassName(s, 'descriptionLabel', classNames({ withDropdownField }))}
                    >
                      {description}
                    </label>

                    {withDropdownField && (
                      <Field
                        name={dropdownFieldName || ''}
                        component={DropdownField}
                        options={dropdownOptions}
                        dropdownPlaceholder={dropdownPlaceholder}
                        validate={dropdownValidate}
                      />
                    )}

                    <FormError error={error || submitError} />

                    {withMessageField && (
                      <>
                        {messageLabel && (
                          <label
                            htmlFor={fieldName}
                            className={s.fieldLabel}
                          >
                            <span>{messageLabel}</span>
                            {withMessageCharacterCounter && (
                              <span className={s.messageCounter}>
                                ({messageCounter}/{MESSAGE_MAX_LENGTH})
                              </span>
                            )}
                          </label>
                        )}
                        <Field
                          name={fieldName}
                          component={TextArea}
                          placeholder={placeholder || `Optional – add a comment about why you have to ${action.replace('_', ' ')}.`}
                          validate={isSendMessage || isCancellation
                            ? messageValidate(action)
                            // Don't validate the field for any other actions
                            // because message field on those forms is optional
                            : undefined}
                          renderErrorMessage={
                            // Show error message on submit for cancel actions only.
                            isCancellation && submitFailed
                          }
                          withIcon={isCancellation}
                          maxLength={withMessageCharacterCounter ? MESSAGE_MAX_LENGTH : undefined}
                        />
                        {isCancellation ? <NeedHelp /> : <CommentMessage />}
                      </>
                    )}
                  </div>
                  {isDeclineInquiry ? (
                    <div className={s.buttons}>
                      <Button
                        type="button"
                        onClick={() => {
                          const errors = form.getState().errors || {};
                          // Validate form before proceeding
                          if (Object.keys(errors).length === 0) {
                            setOfferAnotherDate(true);
                          } else {
                            form.submit(); // This will trigger validation
                          }
                        }}
                        submitting={submitting}
                        classNameModifier="inbox"
                        disabled={submitting}
                        fullWidth
                      >
                        Next
                      </Button>
                      <Button
                        type="reset"
                        onClick={closeBehavior}
                        classNameModifier="inbox secondary"
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <FormButtons
                      classNameModifier="inbox"
                      submitting={submitting}
                      closeModal={closeBehavior}
                      cancelText={cancelText}
                      submitText={submitText || title}
                    />
                  )}
                </>
              )}

              {/* Step 2: Offer Another Date Screen */}
              {offerAnotherDate && (
                <>
                  <div>
                    <img
                      src={boatImage}
                      alt="Boat illustration"
                      className={s.image}
                    />
                    <h3 className={s.title}>
                      Offer Another Date?
                    </h3>
                    <p className={getClassName(s, 'description', 'offerAnotherDate')}>
                      Instead of declining, you can offer an alternative
                      date or time — it can help you secure more
                      bookings and keep customers engaged.
                    </p>
                  </div>
                  <div className={s.buttons}>
                    <Button
                      type="button"
                      onClick={handleOfferAnotherDateClick}
                      classNameModifier="inbox"
                      fullWidth
                    >
                      Offer Another Date
                    </Button>
                    <Button
                      type="submit"
                      submitting={submitting}
                      classNameModifier="inbox secondary"
                      disabled={submitting}
                      fullWidth
                      onClick={handleDeclineInquiry}
                    >
                      Decline Inquiry
                    </Button>
                  </div>
                </>
              )}
            </HotKeys>
          </form>
        );
      }}
    </Form>
  );
};

export default TripActionForm;
